import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LuPlay, LuSave, LuTrash2 } from "react-icons/lu";
import Swal from "sweetalert2";
import ProspectsService from "../../services/ProspectsService";

const getResponseLabel = (item) => item.OpcionRespuesta?.texto || item.option?.texto || "-";
const getCampaignLabel = (item) => item.Campana?.nombre || item.campaign?.nombre || "-";

export default function AutomaticAgendaProspects() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [options, setOptions] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [selectedOptionId, setSelectedOptionId] = useState("");
  const [days, setDays] = useState(1);
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedCampaign = useMemo(
    () => campaigns.find((campaign) => String(campaign.id) === String(selectedCampaignId)),
    [campaigns, selectedCampaignId]
  );

  const loadCampaigns = useCallback(async () => {
    try {
      const response = await ProspectsService.getAllCampaigns();
      const activeCampaigns = (response.data || []).filter((campaign) => campaign.activo);
      setCampaigns(activeCampaigns);
      setSelectedCampaignId((current) => current || (activeCampaigns[0]?.id ? String(activeCampaigns[0].id) : ""));
    } catch {
      Swal.fire("Error", "No se pudieron cargar las campañas.", "error");
    }
  }, []);

  const loadCampaignData = useCallback(async (campaignId) => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const [optionsResponse, configResponse] = await Promise.all([
        ProspectsService.getOptionsByCampaign(campaignId),
        ProspectsService.getAutomaticAgenda(campaignId),
      ]);
      setOptions((optionsResponse.data || []).filter((option) => option.activo));
      setConfigs(configResponse.data || []);
    } catch {
      setOptions([]);
      setConfigs([]);
      Swal.fire("Error", "No se pudo cargar la configuración de agenda automática.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    loadCampaignData(selectedCampaignId);
    setSelectedOptionId("");
    setDays(1);
    setActive(true);
  }, [selectedCampaignId, loadCampaignData]);

  const handleEdit = (config) => {
    setSelectedOptionId(String(config.id_opcion_respuesta));
    setDays(config.dias || 1);
    setActive(Boolean(config.activo));
  };

  const handleSave = async () => {
    if (!selectedCampaignId || !selectedOptionId) {
      Swal.fire("Información", "Seleccioná una campaña y una respuesta de gestión.", "info");
      return;
    }

    const parsedDays = Number(days);
    if (!Number.isInteger(parsedDays) || parsedDays <= 0) {
      Swal.fire("Información", "La cantidad de días debe ser un entero mayor a cero.", "info");
      return;
    }

    setSaving(true);
    try {
      await ProspectsService.saveAutomaticAgenda({
        id_campana: Number(selectedCampaignId),
        id_opcion_respuesta: Number(selectedOptionId),
        dias: parsedDays,
        activo: active,
      });
      Swal.fire("Éxito", "Agenda automática guardada.", "success");
      await loadCampaignData(selectedCampaignId);
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.resp || "No se pudo guardar la agenda automática.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (config) => {
    const result = await Swal.fire({
      title: "Eliminar configuración",
      text: `Se quitará la agenda automática para "${getResponseLabel(config)}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      await ProspectsService.deleteAutomaticAgenda(config.id);
      await loadCampaignData(selectedCampaignId);
      Swal.fire("Eliminada", "La configuración fue eliminada.", "success");
    } catch {
      Swal.fire("Error", "No se pudo eliminar la configuración.", "error");
    }
  };

  const handleRunNow = async () => {
    try {
      const response = await ProspectsService.runAutomaticAgenda();
      Swal.fire("Agenda ejecutada", `Registros reagendados: ${response.data?.recycled || 0}.`, "success");
    } catch {
      Swal.fire("Error", "No se pudo ejecutar la agenda automática.", "error");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Agenda automática</h2>
          <p className="mt-1 text-sm text-slate-500">Reagenda registros según la respuesta de la última gestión.</p>
        </div>
        <button
          type="button"
          onClick={handleRunNow}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-custom-green-ccard-1 px-4 py-2 text-sm font-semibold text-custom-green-ccard-1 hover:bg-custom-green-ccard-1 hover:text-white"
        >
          <LuPlay className="h-4 w-4" />
          Ejecutar ahora
        </button>
      </div>

      <div className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-base font-semibold text-slate-800">Configuración</h3>
          <div className="mt-4 space-y-4">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Campaña</span>
              <select
                value={selectedCampaignId}
                onChange={(event) => setSelectedCampaignId(event.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-custom-green-ccard-1 focus:outline-none"
              >
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.nombre}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Respuesta de gestión</span>
              <select
                value={selectedOptionId}
                onChange={(event) => setSelectedOptionId(event.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-custom-green-ccard-1 focus:outline-none"
              >
                <option value="">Seleccioná una respuesta</option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.texto}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Días para volver a mostrar</span>
              <input
                type="number"
                min="1"
                step="1"
                value={days}
                onChange={(event) => setDays(event.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-custom-green-ccard-1 focus:outline-none"
              />
            </label>

            <label className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={active}
                onChange={(event) => setActive(event.target.checked)}
                className="h-4 w-4 rounded text-custom-green-ccard-1"
              />
              Configuración activa
            </label>

            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-custom-green-ccard-1 px-4 py-2 text-sm font-semibold text-white hover:bg-custom-green-ccard-2 disabled:opacity-60"
            >
              <LuSave className="h-4 w-4" />
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-800">
              {selectedCampaign?.nombre || "Campaña"} 
            </h3>
            {loading ? <span className="text-xs text-slate-500">Cargando...</span> : null}
          </div>
          <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Campaña</th>
                  <th className="px-4 py-3">Respuesta</th>
                  <th className="px-4 py-3">Días</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {configs.map((config) => (
                  <tr key={config.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700">{getCampaignLabel(config)}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{getResponseLabel(config)}</td>
                    <td className="px-4 py-3 text-slate-700">{config.dias}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${config.activo ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {config.activo ? "Activa" : "Inactiva"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(config)}
                          className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(config)}
                          className="rounded-lg border border-red-200 px-3 py-2 text-red-600 hover:bg-red-50"
                          aria-label="Eliminar configuración"
                        >
                          <LuTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!configs.length ? (
                  <tr>
                    <td className="px-4 py-6 text-center text-slate-500" colSpan={5}>
                      Esta campaña todavía no tiene agenda automática configurada.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
